import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanCard = ({ plan, onEdit, onDelete }) => {
  const featuresList = [
    { key: 'bmc', label: 'Business Model Canvas', icon: 'Layout' },
    { key: 'predictiveAnalysis', label: 'Predictive Analysis', icon: 'TrendingUp' },
    { key: 'goalIntelligence', label: 'Goal Intelligence', icon: 'Target' },
    { key: 'decisionIntelligence', label: 'Decision Intelligence', icon: 'Brain' },
    { key: 'reports', label: 'Reports & Analytics', icon: 'FileText' }
  ];

  const getStatusBadge = () => {
    const statusConfig = {
      active: { bg: 'bg-success/10', text: 'text-success', label: 'Active' },
      inactive: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Inactive' }
    };
    const config = statusConfig?.[plan?.status] || statusConfig?.active;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-caption ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden elevation-sm hover:elevation-md transition-smooth">
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-xl font-heading font-bold text-foreground">
              {plan?.name}
            </h3>
            <p className="text-sm text-muted-foreground font-body mt-1">
              {plan?.description}
            </p>
          </div>
          {getStatusBadge()}
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-heading font-bold text-foreground">
              ₹{plan?.monthlyPrice?.toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground font-body">/month</span>
          </div>
          {plan?.yearlyPrice > 0 && (
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-heading font-semibold text-muted-foreground">
                ₹{plan?.yearlyPrice?.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground font-caption">/year</span>
              {plan?.monthlyPrice > 0 && (
                <span className="text-xs text-success font-caption ml-2">
                  Save {Math.round((1 - (plan?.yearlyPrice / (plan?.monthlyPrice * 12))) * 100)}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <h4 className="text-sm font-medium text-foreground font-caption mb-3">
            Features
          </h4>
          <div className="space-y-2">
            {featuresList?.map((feature) => (
              <div key={feature?.key} className="flex items-center gap-2">
                <Icon
                  name={plan?.features?.[feature?.key] ? 'Check' : 'X'}
                  size={14}
                  className={plan?.features?.[feature?.key] ? 'text-success' : 'text-muted-foreground'}
                />
                <span className={`text-xs font-body ${
                  plan?.features?.[feature?.key] ? 'text-foreground' : 'text-muted-foreground line-through'
                }`}>
                  {feature?.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-foreground font-caption mb-3">
            Usage Limits
          </h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-caption">
                Report Downloads
              </span>
              <span className="text-xs font-medium text-foreground font-caption">
                {plan?.limits?.reportDownloads === -1 ? 'Unlimited' : `${plan?.limits?.reportDownloads}/month`}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-caption">
                AI Usage
              </span>
              <span className="text-xs font-medium text-foreground font-caption">
                {plan?.limits?.aiUsagePerMonth === -1 ? 'Unlimited' : `${plan?.limits?.aiUsagePerMonth}/month`}
              </span>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-muted-foreground font-caption">
              Current Subscribers
            </span>
            <span className="text-sm font-medium text-foreground font-caption">
              {plan?.subscribers}
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={onEdit}
              fullWidth
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={onDelete}
              fullWidth
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanCard;